import React, { useState, useEffect } from "react";
import { 
  Users, Phone, Calendar, TrendingUp, Target, Building2, 
  ChevronDown, RefreshCw, ArrowUpRight, ArrowDownRight 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { apiRequest } from "../lib/utils";
import { toast } from "sonner";

export const CREPerformancePage = () => {
  const [activeTab, setActiveTab] = useState("leads");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [leadsPerformance, setLeadsPerformance] = useState(null);
  const [sdrPerformance, setSdrPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranches();
    fetchPerformanceData();
  }, []);

  useEffect(() => {
    fetchPerformanceData();
  }, [selectedBranch, selectedDate]);

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `&branch_id=${selectedBranch}` : "";
      const dateParam = selectedDate ? `?date=${selectedDate}` : "";
      
      const [leadsData, sdrData] = await Promise.all([
        apiRequest(`/performance/leads${dateParam}${branchParam}`),
        apiRequest(`/performance/sdr${dateParam}${branchParam}`)
      ]);
      
      setLeadsPerformance(leadsData);
      setSdrPerformance(sdrData);
    } catch (error) {
      toast.error("Failed to fetch performance data");
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color = "blue" }) => (
    <Card className="bento-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {trend !== undefined && (
              <div className={`flex items-center text-xs mt-1 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                <span>{Math.abs(trend)}% vs yesterday</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg bg-${color}-100`}>
            <Icon className={`w-5 h-5 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="cre-performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">CRE Performance</h1>
          <p className="text-muted-foreground">Track team performance by Lead and SDR pipelines</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[160px]"
            data-testid="date-filter"
          />
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-[180px]" data-testid="branch-filter">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchPerformanceData} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs for Lead vs SDR Performance */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="leads" data-testid="leads-tab">
            <Target className="w-4 h-4 mr-2" />
            Lead Performance
          </TabsTrigger>
          <TabsTrigger value="sdr" data-testid="sdr-tab">
            <Users className="w-4 h-4 mr-2" />
            SDR Performance
          </TabsTrigger>
        </TabsList>

        {/* Lead Performance Tab */}
        <TabsContent value="leads" className="space-y-6">
          {leadsPerformance && (
            <>
              {/* Lead Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total CREs" 
                  value={leadsPerformance.summary.total_cres} 
                  icon={Users}
                  color="blue"
                />
                <StatCard 
                  title="Lead Calls Today" 
                  value={leadsPerformance.summary.total_lead_calls} 
                  icon={Phone}
                  color="green"
                />
                <StatCard 
                  title="Lead Bookings" 
                  value={leadsPerformance.summary.total_lead_bookings} 
                  icon={Calendar}
                  color="purple"
                />
                <StatCard 
                  title="Leads Converted" 
                  value={leadsPerformance.summary.total_leads_converted} 
                  icon={TrendingUp}
                  color="orange"
                />
              </div>

              {/* Lead Performance Table */}
              <Card className="bento-card">
                <CardHeader>
                  <CardTitle className="text-lg">Lead Pipeline Performance by CRE</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="data-table-container">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>CRE Name</TableHead>
                          <TableHead>Branches</TableHead>
                          <TableHead className="text-center">Lead Calls</TableHead>
                          <TableHead className="text-center">Bookings</TableHead>
                          <TableHead className="text-center">Converted</TableHead>
                          <TableHead className="text-center">New Assigned</TableHead>
                          <TableHead className="text-center">In Follow-up</TableHead>
                          <TableHead className="text-center">Conversion %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leadsPerformance.cre_performance.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                              No data available for the selected filters
                            </TableCell>
                          </TableRow>
                        ) : (
                          leadsPerformance.cre_performance.map((cre) => (
                            <TableRow key={cre.cre_id} data-testid={`lead-perf-${cre.cre_id}`}>
                              <TableCell className="font-medium">{cre.cre_name}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {cre.branch_names.map((name, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-mono">{cre.lead_calls}</TableCell>
                              <TableCell className="text-center font-mono">{cre.lead_bookings}</TableCell>
                              <TableCell className="text-center font-mono">{cre.leads_converted}</TableCell>
                              <TableCell className="text-center font-mono">{cre.new_leads_assigned}</TableCell>
                              <TableCell className="text-center font-mono">{cre.leads_in_followup}</TableCell>
                              <TableCell className="text-center">
                                <Badge 
                                  variant={cre.conversion_rate >= 10 ? "default" : "secondary"}
                                  className={cre.conversion_rate >= 10 ? "bg-green-100 text-green-800" : ""}
                                >
                                  {cre.conversion_rate}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* SDR Performance Tab */}
        <TabsContent value="sdr" className="space-y-6">
          {sdrPerformance && (
            <>
              {/* SDR Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total CREs" 
                  value={sdrPerformance.summary.total_cres} 
                  icon={Users}
                  color="blue"
                />
                <StatCard 
                  title="SDR Calls Today" 
                  value={sdrPerformance.summary.total_sdr_calls} 
                  icon={Phone}
                  color="yellow"
                />
                <StatCard 
                  title="SDR Bookings" 
                  value={sdrPerformance.summary.total_sdr_bookings} 
                  icon={Calendar}
                  color="green"
                />
                <StatCard 
                  title="Avg Booking Rate" 
                  value={`${sdrPerformance.summary.avg_booking_rate}%`} 
                  icon={TrendingUp}
                  color="purple"
                />
              </div>

              {/* SDR Performance Table */}
              <Card className="bento-card">
                <CardHeader>
                  <CardTitle className="text-lg">SDR Pipeline Performance by CRE</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="data-table-container">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>CRE Name</TableHead>
                          <TableHead>Branches</TableHead>
                          <TableHead className="text-center">SDR Calls</TableHead>
                          <TableHead className="text-center">Bookings</TableHead>
                          <TableHead className="text-center">Due</TableHead>
                          <TableHead className="text-center">Overdue</TableHead>
                          <TableHead className="text-center">Delayed</TableHead>
                          <TableHead className="text-center">No Show</TableHead>
                          <TableHead className="text-center">Contacted</TableHead>
                          <TableHead className="text-center">Booking %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sdrPerformance.cre_performance.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                              No data available for the selected filters
                            </TableCell>
                          </TableRow>
                        ) : (
                          sdrPerformance.cre_performance.map((cre) => (
                            <TableRow key={cre.cre_id} data-testid={`sdr-perf-${cre.cre_id}`}>
                              <TableCell className="font-medium">{cre.cre_name}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {cre.branch_names.map((name, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-mono">{cre.sdr_calls}</TableCell>
                              <TableCell className="text-center font-mono">{cre.sdr_bookings}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                  {cre.due_assigned}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                  {cre.overdue_assigned}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  {cre.delayed_assigned}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                  {cre.no_show_assigned}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center font-mono">{cre.customers_contacted_today}</TableCell>
                              <TableCell className="text-center">
                                <Badge 
                                  variant={cre.booking_rate >= 5 ? "default" : "secondary"}
                                  className={cre.booking_rate >= 5 ? "bg-green-100 text-green-800" : ""}
                                >
                                  {cre.booking_rate}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CREPerformancePage;
